import { useMemo, useState } from 'react';

import { padColumns } from 'utils/pad-columns';

import type { Order } from 'api';
import { useGetCollectionOrders } from 'api';
import { Restricted } from 'containers/Restricted';

import { COLLECTION_ORDER_STATUS_ENUM } from 'models/order';

import { useOrganisationId, useVirtualAccounts } from 'hooks/context';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useVirtualList } from 'hooks/useVirtualList';

import { DataTable } from 'components/ui/organisms/DataTable';

import { apiParams } from './apiAdapter';
import { getColumns } from './columns';
import { COLLECTION_ORDERS_TABLE_TEST_ID } from './constants';
import { CreatePayinOrdersExportButton } from './CreatePayinOrdersExportButton';
import type { LocalState } from './localState';
import { OrderDetails } from './OrderDetails';

type Props = {
  localState: LocalState;
  updateCursorAfter: (value: string | null) => void;
};

export const CollectionOrdersTable = ({
  localState,
  updateCursorAfter
}: Props) => {
  const organisationId = useOrganisationId<number>();
  const { filters: globalFilters } = useGlobalFilters();

  const [selectedRowData, setSelectedRowData] = useState<Order>();

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const {
    data: orders,
    isFetching,
    refetch
  } = useGetCollectionOrders(
    organisationId,
    apiParams({
      organisation_id: organisationId.toString(),
      ...localState,
      ...globalFilters
    })
  );

  const orderColumns = useMemo(
    () => padColumns(getColumns(findAccountByAccountNumber)),
    [findAccountByAccountNumber]
  );

  const { onScroll, data, optimisticallyUpdateElement } = useVirtualList<Order>(
    {
      data: orders?.data,
      initialRefValue: orders?.meta?.cursor_after ?? undefined,
      uniqueKey: 'uuid',
      isFetching,
      updateCursorCallback: updateCursorAfter,
      resetConditions: [globalFilters, localState.filters, updateCursorAfter],
      cursorNext: orders?.meta?.cursor_after
        ? orders.meta?.cursor_after
        : undefined
    }
  );

  const handleRowClick = (record: Order) => {
    setSelectedRowData((selected) =>
      selected !== record ? record : undefined
    );
  };

  return (
    <DataTable
      total={orders?.meta?.total}
      tableActions={
        <Restricted requiredPermission="exports:manage">
          <CreatePayinOrdersExportButton
            filters={localState.filters}
            dateRange={globalFilters.dateRange}
          />
        </Restricted>
      }
      dataSource={data}
      columns={orderColumns}
      onScroll={onScroll}
      onRowClick={handleRowClick}
      isLoading={isFetching}
      rowKey="uuid"
      selectedRow={selectedRowData}
      tableDataTestId={COLLECTION_ORDERS_TABLE_TEST_ID}
      rowDataTestId="orders-table-row-entry"
      rightPanel={
        selectedRowData ? (
          <OrderDetails
            key={selectedRowData.uuid}
            orderId={selectedRowData.uuid!}
            onClose={() => setSelectedRowData(undefined)}
            onCancel={() =>
              optimisticallyUpdateElement({
                ...selectedRowData,
                status: COLLECTION_ORDER_STATUS_ENUM.CANCELLED
              })
            }
            onRefundSuccess={() =>
              optimisticallyUpdateElement({
                ...selectedRowData,
                status: COLLECTION_ORDER_STATUS_ENUM.PROCESSING_REFUND
              })
            }
            onTestCaseSuccess={refetch}
          />
        ) : undefined
      }
    />
  );
};
